import * as Phaser from "phaser-ce"
import { CCUtils } from "../../common/CCUtils"
import { COLOR } from "../../common/Color"
import { log } from "../../common/Logging"
import { FontType } from "../../common/TextUtils"
import { colorNum } from "../utils/ColorUtils"
import { PhaserText, PhaserTextConfig } from "../utils/PhaserText"
import { PhaserUtils } from "../utils/PhaserUtils"
import { Scale } from "../utils/Scale"
import { Sprites } from "../utils/Sprites"
import { buttonAsset } from "./GenericAssets"

export class ButtonContainer {
    group: Phaser.Group
    button: Phaser.Button
    text: Phaser.Text

    constructor(
        private game: Phaser.Game,
        group: Phaser.Group,
        button: Phaser.Button,
        text: Phaser.Text,
    ) {
        this.group = group
        this.button = button
        this.text = text
    }

    center = () => {
        Sprites.centerSpriteInSprite(this.button, this.text)
        this.text.y += Scale.height() * 0.004
    }

    centerInWorldX = () => {
        Sprites.centerInWorldXOnly(this.button)
        Sprites.centerInWorldXOnly(this.text)
    }

    enable = (enable = true) => {
        if (this.button) {
            if (enable) {
                this.button.inputEnabled = true
                this.button.tint = colorNum.white
                this.text.tint = colorNum.white
                this.button.input.useHandCursor = true
            } else {
                this.disable()
            }
        }
    }

    grayOut = () => {
        this.button.tint = colorNum.gray
        this.text.tint = colorNum.gray
    }

    disable = () => {
        this.button.tint = colorNum.gray
        this.text.tint = colorNum.gray
        this.button.inputEnabled = false
        this.game.canvas.style.cursor = ""
    }

    destroy = () => {
        this.button.destroy()
        this.text.destroy()
    }

    setOnClick = (onClick: () => void) =>
        this.button.events.onInputDown.add(onClick)

    setOnClickOnce = (onClick: () => void) =>
        this.button.events.onInputDown.addOnce(onClick)

    removeEvents = () => this.button.events.onInputDown.removeAll()

    bringToTop = () => {
        this.button.bringToTop()
        this.text.bringToTop()
    }

    setInvisible = () => {
        this.button.visible = false
        this.text.visible = false
    }

    setVisible = () => {
        this.button.visible = true
        this.text.visible = true
    }

    enableDebug = () => {
        if (CCUtils.debug) {
            this.button.inputEnabled = true
            this.button.input.enableDrag()
            this.button.events.onDragStop.add(() => {
                log.debug(
                    `Button ${this.button.key} is at \n${PhaserUtils.coors(
                        this.button.x,
                        this.button.y,
                        1000,
                    )}`,
                )
                this.center()
            })
        }
    }
}

export enum ButtonType {
    PRIMARY = "PRIMARY",
    SECONDARY = "SECONDARY",
    GHOST = "GHOST",
}

export enum ButtonSize {
    NORMAL = "NORMAL",
    SQUARE = "SQUARE",
    WIDE = "WIDE",
    EXTRA_WIDE = "EXTRA_WIDE",
}

export interface ButtonsOptionsConfig {
    x?: number
    y?: number
    width?: number
    textConfig?: PhaserTextConfig
    centerInWorldX?: boolean
    enabled?: boolean
    group?: Phaser.Group
    buttonType?: ButtonType
    buttonSize?: ButtonSize
    scale?: number
}

export class Buttons {
    static create = (
        game: Phaser.Game,
        content: string,
        onClick?: () => void,
        {
            x = 0,
            y = 0,
            width,
            textConfig,
            centerInWorldX = false,
            enabled = true,
            group,
            buttonType = ButtonType.PRIMARY,
            buttonSize = ButtonSize.NORMAL,
            scale = 0.75,
            debug,
        }: {
            x?: number
            y?: number
            width?: number
            textConfig?: PhaserTextConfig
            centerInWorldX?: boolean
            enabled?: boolean
            group?: Phaser.Group
            buttonType?: ButtonType
            buttonSize?: ButtonSize
            scale?: number
            debug?: boolean
        } = {},
    ): ButtonContainer => {
        if ((width !== undefined && width > 1) || width < 0) {
            throw Error("Width must be a percent.")
        }
        if (group === undefined) {
            group = game.add.group()
        }
        const asset = buttonAsset(buttonType, buttonSize)
        let textConfigMod = textConfig
        let onHover: () => void
        let onExitHover: () => void
        let onPress: () => void
        let onPressDone: () => void
        if (buttonType === ButtonType.GHOST) {
            textConfigMod = { ...textConfig, color: COLOR.primaryLight }
            onHover = () => {
                text.addColor(COLOR.primary, 0)
            }
            onExitHover = () => {
                text.addColor(COLOR.primaryLight, 0)
            }
            onPress = () => {
                text.addColor(COLOR.primaryDark, 0)
            }
            onPressDone = onExitHover
        } else if (buttonType === ButtonType.PRIMARY) {
            textConfigMod = { color: COLOR.white, ...textConfig }
        }
        if (!textConfigMod || textConfigMod.type === FontType.BODY) {
            textConfigMod = { ...textConfigMod, weight: "bold" }
        }

        const button = new Phaser.Button(
            game,
            x,
            y,
            asset,
            onClick,
            undefined,
            1,
            0,
            2,
        )
        if (onPress) {
            button.events.onInputDown.add(onPress)
            button.events.onInputOver.add(onHover)
            button.events.onInputOut.add(onExitHover)
            button.events.onInputUp.add(onPressDone)
        }

        Scale.scaleSprite(button, scale)
        if (width) {
            button.width = Scale.x(width)
        }
        group.add(button)
        const text = PhaserText.create(game, content, {
            fontPx: 24 * scale,
            ...textConfigMod,
        })
        group.add(text)
        if (centerInWorldX) {
            Sprites.centerInWorldXOnly(button)
        }
        const buttonContainer = new ButtonContainer(game, group, button, text)
        buttonContainer.center()
        if (!enabled) {
            log.debug("Disabling button because enabled was false.")
            buttonContainer.disable()
        }
        if (CCUtils.debug && debug) {
            buttonContainer.enableDebug()
        }
        return buttonContainer
    }

    static makeSpriteButton = (
        sprite: Phaser.Sprite | Phaser.Button | Phaser.Image,
        onClick?: () => void,
    ) => {
        sprite.inputEnabled = true
        sprite.input.useHandCursor = true
        if (onClick) {
            sprite.events.onInputDown.add(onClick)
        }
    }

    static undoSpriteButton = (sprite: Phaser.Sprite | Phaser.Button) => {
        sprite.inputEnabled = false
        sprite.events.onInputDown.removeAll()
    }
}
